import React from 'react'
import { motion } from 'framer-motion'
import { UserPlus, Edit, Trash2, Shield, Key } from 'lucide-react'
import { useLanguage } from '@/contexts/LanguageContext'
import type { useUserManagementViewModel } from '../useUserManagementViewModel'
import { formatRelativeTime, formatFullDateTime, formatShortDate } from '@/utils/dateFormat'

type VM = ReturnType<typeof useUserManagementViewModel>

interface UserTableProps {
  loading: VM['loading']
  filteredUsers: VM['filteredUsers']
  searchTerm: VM['searchTerm']
  hasAdminAccess: VM['hasAdminAccess']
  currentUser: VM['currentUser']
  handleEditUser: VM['handleEditUser']
  handleChangePassword: VM['handleChangePassword']
  handleDeleteUser: VM['handleDeleteUser']
}

const UserTable: React.FC<UserTableProps> = ({
  loading,
  filteredUsers,
  searchTerm,
  hasAdminAccess,
  currentUser,
  handleEditUser,
  handleChangePassword,
  handleDeleteUser,
}) => {
  const { t } = useLanguage()

  return (
    <>
        {loading ? (
          <div className="bg-white rounded-lg shadow-sm p-8">
            <div className="flex items-center justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <span className="ml-3 text-gray-600">{t.loading.usersLoading}</span>
            </div>
          </div>
        ) : filteredUsers.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm p-8">
            <div className="text-center">
              <UserPlus className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {searchTerm ? 'Kullanıcı bulunamadı' : 'Henüz kullanıcı yok'}
              </h3>
              <p className="text-gray-500">
                {searchTerm ? 'Arama kriterlerinizi değiştirin' : 'İlk kullanıcıyı eklemek için başlayın'}
              </p>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            {/* Table Header */}
            <div className="bg-gray-50 px-6 py-4 border-b">
              <div className="grid grid-cols-[1.8fr_2fr_1.1fr_1.6fr_1.3fr_0.7fr_1.1fr_0.9fr_1.2fr] gap-4 text-sm font-medium text-gray-900">
                <div>Kullanıcı</div>
                <div>E-posta</div>
                <div>Rol</div>
                <div>Şirket</div>
                <div>Son Giriş</div>
                <div className="text-center">Giriş</div>
                <div>Kayıt</div>
                <div>Durum</div>
                <div>İşlemler</div>
              </div>
            </div>

            {/* Table Body */}
            <div className="divide-y divide-gray-200">
                {filteredUsers.map((user) => (
                <motion.div
                  key={user.id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="px-6 py-4 hover:bg-gray-50"
                >
                  <div className="grid grid-cols-[1.8fr_2fr_1.1fr_1.6fr_1.3fr_0.7fr_1.1fr_0.9fr_1.2fr] gap-4 items-center">
                    <div>
                      <div className="flex items-center">
                        <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                          <span className="text-blue-600 font-semibold text-sm">
                            {user.first_name.charAt(0)}{user.last_name.charAt(0)}
                          </span>
                      </div>
                        <div className="ml-3 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {user.first_name} {user.last_name}
                          </p>
                          <p className="text-xs text-gray-500">ID: {user.id}</p>
                        </div>
                      </div>
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm text-gray-900 truncate" title={user.email}>{user.email}</p>
                    </div>
                    <div>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        user.role === 'superadmin'
                                                    ? 'bg-red-100 text-red-800'
                          : user.role === 'admin'
                          ? 'bg-purple-100 text-purple-800'
                          : user.role === 'customer'
                          ? 'bg-green-100 text-green-800'
                          : 'bg-blue-100 text-blue-800'
                      }`}>
                        <Shield className="w-3 h-3 mr-1" />
                        {user.role === 'superadmin' ? 'Süper Yönetici' :
                         user.role === 'admin' ? 'Yönetici' :
                         user.role === 'customer' ? 'Müşteri' : 'Operatör'}
                      </span>
                        </div>
                    <div className="min-w-0">
                      <p className="text-sm text-gray-900 truncate" title={user.company_name || 'Belirsiz'}>{user.company_name || 'Belirsiz'}</p>
                      </div>
                    <div>
                      {user.last_login_at ? (
                        <p className="text-sm text-gray-900" title={formatFullDateTime(user.last_login_at)}>
                          {formatRelativeTime(user.last_login_at)}
                        </p>
                      ) : (
                        <p className="text-sm text-gray-400 italic">Hiç giriş yapmadı</p>
                      )}
                    </div>
                    <div className="text-center">
                      <span className="inline-flex items-center justify-center min-w-[2rem] px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                        {user.login_count ?? 0}
                      </span>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600" title={formatFullDateTime(user.created_at)}>
                        {formatShortDate(user.created_at)}
                      </p>
                    </div>
                    <div>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        user.is_active
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-600'
                      }`}>
                        {user.is_active ? 'Aktif' : 'Pasif'}
                      </span>
                    </div>
                    <div>
                      <div className="flex items-center space-x-2">
                        {/* SuperAdmin (ID 35) cannot be edited by anyone */}
                        {user.id !== 35 && (
                        <button
                        onClick={() => handleEditUser(user)}
                          className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          title="Düzenle"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        )}
                        {user.id !== 35 && (
                        <button
                          onClick={() => handleChangePassword(user)}
                          className="p-1.5 text-gray-400 hover:text-orange-600 hover:bg-orange-50 rounded-lg transition-colors"
                          title="Şifre Değiştir"
                        >
                          <Key className="w-4 h-4" />
                        </button>
                        )}
                        {hasAdminAccess && user.id !== currentUser?.id && user.id !== 35 && (
                          <button
                            onClick={() => handleDeleteUser(user)}
                            className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Sil"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
          </div>
        </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        )}
    </>
  )
}

export default UserTable
